import React, { useEffect, useRef, useState } from 'react';
import { createHotspot, disposeHotspot } from '../../utils/threeUtils';
import * as THREE from 'three';

const Hotspot = ({ position, scale, inventory, onHotspotClick, sceneType, timer, hotspotUuid, type, name, destination, action }) => {
  const hotspotRef = useRef(null);
  const [isSceneReady, setIsSceneReady] = useState(false);
  const [shouldCreateHotspot, setShouldCreateHotspot] = useState(false);
  const [hasSceneBeenReady, setHasSceneBeenReady] = useState(false); // Track if scene was ever ready
  const timerRef = useRef(null);
  const videoCheckIntervalRef = useRef(null);

  // Check if scene is ready (scene loaded and Three.js initialized)
  useEffect(() => {
    const checkSceneReady = () => {
      const hasThreeJS = window._threeDViewerScene && window._threeDViewerCamera && window._threeDViewerRenderer;
      const hasCanvas = document.querySelector('.ThreeDViewerC canvas');
      
      // Check if loader is hidden by looking at computed styles instead of inline styles
      const loaderOverlay = document.querySelector('.loader-overlay');
      const isLoaderHidden = !loaderOverlay || 
                            getComputedStyle(loaderOverlay).display === 'none' ||
                            getComputedStyle(loaderOverlay).visibility === 'hidden';
      
      const ready = hasThreeJS && hasCanvas && isLoaderHidden;
      
      return ready;
    };

    const checkReady = () => {
      const ready = checkSceneReady();
      
      // Once scene is ready, keep it ready to prevent flickering
      if (ready && !hasSceneBeenReady) {
        setHasSceneBeenReady(true);
      }
      
      // Use the stable ready state
      const stableReady = ready || hasSceneBeenReady;
      setIsSceneReady(stableReady);
      
      // Only create hotspot immediately if no timer is provided or timer is 0
      if (stableReady && !shouldCreateHotspot && (!timer || timer === 0)) {
        setShouldCreateHotspot(true);
      }
    };

    // Initial check
    checkReady();

    // Set up observer for loader changes
    const observer = new MutationObserver(checkReady);
    const viewerElement = document.querySelector('.ThreeDViewerC');
    
    if (viewerElement) {
      observer.observe(viewerElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class']
      });
    }

    // Periodic check as fallback - but less frequent to prevent flickering
    const interval = setInterval(checkReady, 1000); // Changed from 500ms to 1000ms

    return () => {
      observer.disconnect();
      clearInterval(interval);
    };
  }, [timer, shouldCreateHotspot, hotspotUuid, hasSceneBeenReady]);

  // Handle timer logic for all scene types
  useEffect(() => {
    if (!isSceneReady || !timer || timer === 0) return;

    const startTimer = () => {
      // Clear any existing timer
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      // Set timer based on timer value (in seconds)
      const delayMs = timer * 1000;
      timerRef.current = setTimeout(() => {
        setShouldCreateHotspot(true);
      }, delayMs);
    };

    // For video scenes, we need to wait for video to start playing
    if (sceneType === 'VIDEO') {
      const video = window._threeDViewerVideo;
      if (!video) {
        // If video element doesn't exist yet, wait for it
        const checkVideo = () => {
          const videoElement = window._threeDViewerVideo;
          if (videoElement) {
            setupVideoTimer(videoElement);
          } else {
            // Check again in 100ms
            setTimeout(checkVideo, 100);
          }
        };
        checkVideo();
        return;
      }

      const setupVideoTimer = (videoElement) => {
        // Helper to show/hide hotspot based on current time
        const updateHotspotVisibility = () => {
          if (videoElement.currentTime >= timer) {
            setShouldCreateHotspot(true);
          } else {
            setShouldCreateHotspot(false);
          }
        };

        const handlePlay = () => {
          updateHotspotVisibility();
          // If not yet visible, start a timer for the remaining time
          if (videoElement.currentTime < timer) {
            const remaining = (timer - videoElement.currentTime) * 1000;
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => {
              setShouldCreateHotspot(true);
            }, remaining);
          }
        };

        const handlePause = () => {
          if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
          }
        };

        const handleEnded = () => {
          if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
          }
          // If looping, the 'play' event will fire again
        };

        const handleSeeked = () => {
          updateHotspotVisibility();
          if (videoElement.currentTime < timer) {
            setShouldCreateHotspot(false);
            if (timerRef.current) clearTimeout(timerRef.current);
            const remaining = (timer - videoElement.currentTime) * 1000;
            timerRef.current = setTimeout(() => {
              setShouldCreateHotspot(true);
            }, remaining);
          } else {
            setShouldCreateHotspot(true);
            if (timerRef.current) {
              clearTimeout(timerRef.current);
              timerRef.current = null;
            }
          }
        };

        const handleTimeUpdate = () => {
          // If user scrubs back before timer, hide hotspot
          if (videoElement.currentTime < timer && shouldCreateHotspot) {
            setShouldCreateHotspot(false);
            if (timerRef.current) clearTimeout(timerRef.current);
            const remaining = (timer - videoElement.currentTime) * 1000;
            timerRef.current = setTimeout(() => {
              setShouldCreateHotspot(true);
            }, remaining);
          }
        };

        videoElement.addEventListener('play', handlePlay);
        videoElement.addEventListener('pause', handlePause);
        videoElement.addEventListener('ended', handleEnded);
        videoElement.addEventListener('seeked', handleSeeked);
        videoElement.addEventListener('timeupdate', handleTimeUpdate);

        // Initial check
        updateHotspotVisibility();
        if (!videoElement.paused) {
          handlePlay();
        }

        return () => {
          videoElement.removeEventListener('play', handlePlay);
          videoElement.removeEventListener('pause', handlePause);
          videoElement.removeEventListener('ended', handleEnded);
          videoElement.removeEventListener('seeked', handleSeeked);
          videoElement.removeEventListener('timeupdate', handleTimeUpdate);
          if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
          }
        };
      };

      return setupVideoTimer(video);
    } else {
      // For non-video scenes (IMAGE, etc.), start timer immediately
    
      startTimer();

      return () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
      };
    }
  }, [isSceneReady, timer, sceneType]);

  // Reset state when scene changes
  useEffect(() => {
    setShouldCreateHotspot(false);
    setHasSceneBeenReady(false); // Reset stable state for new scene
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (videoCheckIntervalRef.current) {
      clearInterval(videoCheckIntervalRef.current);
      videoCheckIntervalRef.current = null;
    }
  }, [sceneType, hotspotUuid]);

  // Handle timer changes separately to avoid unnecessary resets
  useEffect(() => {
    if (isSceneReady && timer !== undefined) {
      // Clear existing timer
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      
      // If timer is 0 or undefined, show hotspot immediately
      if (!timer || timer === 0) {
        setShouldCreateHotspot(true);
      } else {
        // For video scenes, the timer logic is handled in the video timer effect
        // For other scenes, start the timer
        if (sceneType !== 'VIDEO') {
          const delayMs = timer * 1000;
          timerRef.current = setTimeout(() => {
            setShouldCreateHotspot(true);
          }, delayMs);
        }
      }
    }
    
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [timer, isSceneReady, sceneType]);

  // Create hotspot when conditions are met
  useEffect(() => {
    if (!shouldCreateHotspot || !isSceneReady || !window._threeDViewerScene || !window._threeDViewerCamera || !window._threeDViewerRenderer) {
      return;
    }

    // Prevent creating multiple hotspots for the same inventory
    if (hotspotRef.current) {
      return;
    }

    // Prevent recreation if hotspot was already created and is stable
    if (hasSceneBeenReady && hotspotRef.current) {
      return;
    }

    hotspotRef.current = createHotspot(
      window._threeDViewerScene,
      window._threeDViewerCamera,
      window._threeDViewerRenderer,
      position,
      scale,
      inventory,
      onHotspotClick,
      undefined, // onHover
      undefined, // onTextureLoaded
      hotspotUuid // Pass the hotspot UUID
    );

    return () => {
      if (hotspotRef.current) {
        disposeHotspot(hotspotRef.current);
        hotspotRef.current = null;
      }
    };
  }, [shouldCreateHotspot, isSceneReady, hotspotUuid, hasSceneBeenReady]);

  // Update position and scale when props change
  useEffect(() => {
    if (hotspotRef.current) {
      
      // Update position
      hotspotRef.current.position.set(position.x, position.y, position.z);
      
      // Update scale
      const newScale = new THREE.Vector3(scale.x, scale.y, scale.x);
      hotspotRef.current.scale.copy(newScale);
      if (hotspotRef.current.userData.originalScale) {
        hotspotRef.current.userData.originalScale.copy(newScale);
      }
      
      // Force render update to ensure changes are visible
      if (window._threeDViewerRenderer && window._threeDViewerScene && window._threeDViewerCamera) {
        window._threeDViewerRenderer.render(window._threeDViewerScene, window._threeDViewerCamera);
      }
    }
  }, [position.x, position.y, position.z, scale.x, scale.y, scale.z, hotspotUuid]);

  // Handle inventory changes without recreating hotspot
  useEffect(() => {
    if (hotspotRef.current && inventory?.uuid) {
      
      // Update the inventory reference in userData
      hotspotRef.current.userData.inventory = inventory;
      
      // Update the hotspot texture if the inventory path has changed
      if (inventory.path && hotspotRef.current.material) {
        // Import the updateHotspotTexture function dynamically to avoid circular imports
        import('../../utils/threeUtils').then(({ updateHotspotTexture }) => {
          updateHotspotTexture(hotspotRef.current, inventory.path, window._threeDViewerRenderer);
        }).catch(error => {
          console.error('Error updating hotspot texture:', error);
        });
      }
    }
  }, [inventory?.uuid, inventory?.path, hotspotUuid]);

  // Handle hotspot type changes
  useEffect(() => {
    if (hotspotRef.current && type) {
      
      // Update the type in userData
      hotspotRef.current.userData.type = type;
      
      // If the type has changed significantly, we might need to recreate the hotspot
      const previousType = hotspotRef.current.userData.previousType;
      if (previousType && previousType !== type) {
        
        // For now, just update the previous type reference
        // In the future, this could trigger a complete recreation if needed
        hotspotRef.current.userData.previousType = type;
      } else if (!previousType) {
        // First time setting the type
        hotspotRef.current.userData.previousType = type;
      }
      
      // Force render update to ensure changes are visible
      if (window._threeDViewerRenderer && window._threeDViewerScene && window._threeDViewerCamera) {
        window._threeDViewerRenderer.render(window._threeDViewerScene, window._threeDViewerCamera);
      }
    }
  }, [type, hotspotUuid]);

  // Handle hotspot name changes
  useEffect(() => {
    if (hotspotRef.current && name) {
      // Update the name in userData
      hotspotRef.current.userData.name = name;
    }
  }, [name, hotspotUuid]);

  // Handle hotspot destination changes (for LINK type hotspots)
  useEffect(() => {
    if (hotspotRef.current && destination !== undefined) {
      // Update the destination in userData
      hotspotRef.current.userData.destination = destination;
    }
  }, [destination, hotspotUuid]);

  // Handle hotspot action changes
  useEffect(() => {
    if (hotspotRef.current && action) {
      // Update the action in userData
      hotspotRef.current.userData.action = action;
    }
  }, [action, hotspotUuid]);

  return null;
};

export default Hotspot; 